import { Preferences } from '../../../profile/objects/preferences';
import { Column } from '../objects/column';

export type SetListPreferenceOptions = {
    // Identifiant du module (= Preferences.module). Filtre les préférences de cet écran.
    module: string;
    // Source de vérité des colonnes (DEFAULT_*_COLUMNS). On NE duplique jamais les colonnes dans un switch.
    default_columns: Column[];
    // Tri par défaut quand aucune préférence ne porte d'ordre.
    default_order_by?: string;
    default_order?: 'asc' | 'desc';
};

export type ListPreferenceResult = {
    order_by: string;
    order: 'asc' | 'desc';
    columns: Column[];
    preferences: Preferences[];
};

/**
 * Transforme les préférences backend en configuration de liste (colonnes ordonnées + tri),
 * en mergeant chaque préférence sur sa définition de colonne (visibilité, largeur).
 *
 * Générique : chaque feature fournit son module + ses DEFAULT_*_COLUMNS + son tri par défaut.
 * Remplace les anciens setXxxPreference qui dupliquaient les colonnes dans un gros switch.
 */
export const setListPreference = (
    new_preferences: Preferences[],
    state_columns: Column[],
    options: SetListPreferenceOptions,
): ListPreferenceResult => {
    const column_map: Record<string, Column> = Object.fromEntries(
        options.default_columns.map(col => [col.field_name, col]),
    );
    const seen_field_names: string[] = [];
    const columns: Column[] = [];
    const preferences: Preferences[] = [];
    let order_by = options.default_order_by ?? '';
    let order: 'asc' | 'desc' = options.default_order ?? 'asc';

    new_preferences.forEach((preference: Preferences) => {
        if (preference.module !== options.module) return;
        if (seen_field_names.includes(preference.field_name)) return;

        const col_def = column_map[preference.field_name];
        if (col_def) {
            columns.push({ ...col_def, checked: preference.active, width: preference.width ?? null, last_updated: false });
            if (preference.ordering_asc !== null && col_def.order_by) order_by = col_def.order_by;
        }

        preferences.push(preference);
        seen_field_names.push(preference.field_name);
        order = preference.ordering_asc !== null
            ? preference.ordering_asc ? 'asc' : 'desc'
            : order;
    });

    if (columns.length !== state_columns.length) {
        state_columns.forEach(column => {
            if (!columns.some(c => c.field_name === column.field_name)) {
                columns.push({ ...column });
            }
        });
    }

    return { order_by, order, columns, preferences };
};
